import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Form } from "../widgets";
import { DataTable } from "../widgets";
import { useLoadHistoryContext } from "./context/LoadHistoryContext";

const LoadBulkEditRow = ({ showableCustomColumns, initialHeaders }) => {
  const {
    selectedCrew,
    setSelectedCrew,
    selectedField,
    setSelectedField,
    selectedOffload,
    setSelectedOffload,
    selectedStorage,
    setSelectedStorage,
    selectedPipe,
    setSelectedPipe,
    // loadWeight, setLoadWeight,
    loadGrossWeight,
    setLoadGrossWeight,
    loadTareWeight,
    setLoadTareWeight,
    loadLightWeight,
    setLoadLightWeight,
    bulkEditLoadList,
    handleBulkEditLoadSelect,
  } = useLoadHistoryContext();

  const geoShapeList = useSelector(
    (state) => state.equipmentGroup.geoShapeList
  );
  const equipmentGroupList = useSelector(
    (state) => state.equipmentGroup.equipmentGroupList
  );

  const fieldOptions = useMemo(() => {
    const fieldList = geoShapeList.filter(
      (e) => e.geo_region_type_id === 2 && e.active
    );
    return fieldList.map((e) => ({
      value: e.geo_shape_uuid,
      label: e.geo_shape_name,
    }));
  }, [geoShapeList]);

  const { offloadOptions, offloadSiteList } = useMemo(() => {
    const offloadSiteList = geoShapeList.filter(
      (e) => e.geo_region_type_id === 4 && e.active
    );
    const offloadOptions = offloadSiteList.map((e) => ({
      value: e.geo_shape_uuid,
      label: e.geo_shape_name,
    }));

    return { offloadOptions, offloadSiteList };
  }, [geoShapeList]);

  const storageOptions = useMemo(() => {
    const selectedOffloadId = (selectedOffload || {}).value;
    if (!selectedOffloadId) return [];

    const selectedOffloadInfo = offloadSiteList.find(
      (e) => e.geo_shape_uuid === selectedOffloadId
    );
    const storageList = Array.isArray(selectedOffloadInfo.geo_storage_locations)
      ? selectedOffloadInfo.geo_storage_locations
      : [];

    if (storageList.length === 0) {
      return [
        {
          value: "default",
          label: "Default",
        },
      ];
    }

    return storageList.map((e) => ({
      value: e.geo_storage_location_uuid,
      label: e.storage_location_name,
    }));
  }, [offloadSiteList, selectedOffload]);

  const pipeOptions = useMemo(() => {
    if (!selectedOffload || !selectedStorage) return [];
    const geoStorageLocations = {};

    offloadSiteList &&
      offloadSiteList.forEach((item) => {
        if (item.geo_storage_locations) {
          for (let storage of item.geo_storage_locations) {
            let pipeList = [];
            if (storage.pipe > 0) {
              pipeList = Array.from({ length: storage.pipe }, (item, index) => {
                const pipe = {
                  value: index + 1,
                  label: `Pipe ${index + 1}`,
                };
                return pipe;
              });
            }
            geoStorageLocations[storage.geo_storage_location_uuid] = pipeList;
          }
        }
      });
    const storagePipeList =
      geoStorageLocations && geoStorageLocations[selectedStorage.value]
        ? geoStorageLocations[selectedStorage.value]
        : [];

    if (storagePipeList.length === 0) {
      return [
        {
          value: "default",
          label: "Default",
        },
      ];
    }

    return storagePipeList;
  }, [offloadSiteList, selectedStorage]);

  useEffect(() => {
    setSelectedStorage(storageOptions.length > 0 ? storageOptions[0] : null);
  }, [storageOptions]);

  useEffect(() => {
    setSelectedPipe(pipeOptions.length > 0 ? pipeOptions[0] : null);
  }, [pipeOptions]);

  useEffect(() => {
    handleBulkEditLoadSelect(bulkEditLoadList, true);
  }, [
    selectedCrew,
    selectedField,
    selectedOffload,
    selectedStorage,
    selectedPipe,
    loadGrossWeight,
    loadTareWeight,
    loadLightWeight,
  ]);

  const crewList = useMemo(() => {
    const key = "crew_uuid";
    const list = equipmentGroupList
      .filter((e) => e.crew.name !== "" && e.alive)
      .map((e) => e.crew);
    const crewList = [
      ...new Map(list.map((item) => [item[key], item])).values(),
    ];

    return [...crewList.map((e) => ({ value: e.crew_uuid, label: e.name }))];
  }, [equipmentGroupList]);

  const updatedRows = showableCustomColumns.map((column, columnIndex) => {
    const header = initialHeaders.find(
      (col) => col.key === column && col.isShow
    );
    if (!header) {
      return null; // Handle missing columns
    }
    switch (column) {
      case "crew_name":
        return (
          <DataTable.Cell key={columnIndex}>
            <Form.Select
              id="crew"
              placeholder="Crew"
              name="crewId"
              onChange={(e) => {
                if (e) setSelectedCrew({ crewId: e.value, crewName: e.label });
                else setSelectedCrew(e);
              }}
              value={(selectedCrew || {}).crewId}
              options={crewList}
              sortType="asc"
            />
          </DataTable.Cell>
        );
      case "field_geo_shape_name":
        return (
          <DataTable.Cell key={columnIndex}>
            <Form.Select
              id="field"
              placeholder="Field"
              name="fieldId"
              onChange={(e) => {
                if (e) {
                  const findShape =
                    geoShapeList.find(
                      (item) => item.geo_shape_uuid === e.value
                    ) || {};
                  setSelectedField({
                    ...e,
                    regionId: findShape.geo_region_uuid,
                    regionName: findShape.geo_region_name,
                  });
                } else {
                  setSelectedField(e);
                }
              }}
              value={(selectedField || {}).value}
              options={fieldOptions}
              sortType="asc"
            />
          </DataTable.Cell>
        );
      case "offload_geo_shape_name":
        return (
          <DataTable.Cell key={columnIndex}>
            <Form.Select
              id="offload"
              placeholder="Offload Site"
              name="offloadId"
              onChange={(e) => {
                if (e) {
                  const findShape =
                    geoShapeList.find(
                      (item) => item.geo_shape_uuid === e.value
                    ) || {};
                  setSelectedOffload({
                    ...e,
                    regionId: findShape.geo_region_uuid,
                    regionName: findShape.geo_region_name,
                  });
                } else {
                  setSelectedOffload(e);
                }
              }}
              value={(selectedOffload || {}).value}
              options={offloadOptions}
              sortType="asc"
            />
          </DataTable.Cell>
        );
      case "offload_geo_storage_location_name":
        return (
          <DataTable.Cell key={columnIndex}>
            <Form.Select
              id="storage"
              placeholder="Storage"
              name="storageId"
              onChange={(e) => setSelectedStorage(e)}
              value={(selectedStorage || {}).value}
              options={storageOptions}
            />
          </DataTable.Cell>
        );
      case "pipe":
        return (
          <DataTable.Cell key={columnIndex}>
            <Form.Select
              id="pipe"
              placeholder="pipe"
              name="pipeId"
              onChange={(e) => setSelectedPipe(e)}
              value={(selectedPipe || {}).value}
              options={pipeOptions}
            />
          </DataTable.Cell>
        );
      case "load_gross_weight":
        return (
          <DataTable.Cell key={columnIndex}>
            <Form.Control
              id="loadGrossWeight"
              value={loadGrossWeight}
              onChange={(e) => setLoadGrossWeight(e.target.value)}
              min={0}
              max={1000000}
              type="number"
              placeholder="Gross Weight"
              className="form-control edit-row-input"
              autoComplete="off"
              name="loadGrossWeight"
            />
          </DataTable.Cell>
        );
      case "tare_weight":
        return (
          <DataTable.Cell key={columnIndex}>
            <Form.Control
              id="loadTareWeight"
              value={loadTareWeight}
              onChange={(e) => setLoadTareWeight(e.target.value)}
              min={0}
              max={1000000}
              type="number"
              placeholder="Tare Weight"
              className="form-control edit-row-input"
              autoComplete="off"
              name="loadTareWeight"
            />
          </DataTable.Cell>
        );
      case "light_weight":
        return (
          <DataTable.Cell key={columnIndex}>
            <Form.Control
              id="loadLightWeight"
              value={loadLightWeight}
              onChange={(e) => setLoadLightWeight(e.target.value)}
              min={0}
              max={1000000}
              type="number"
              placeholder="Light Weight"
              className="form-control edit-row-input"
              autoComplete="off"
              name="loadLightWeight"
            />
          </DataTable.Cell>
        );
      default:
        return <DataTable.Cell key={columnIndex} />;
    }
  });

  return (
    <DataTable.Row className="load-editable-row">
      <DataTable.Cell />
      {updatedRows}
    </DataTable.Row>
  );
};

export default LoadBulkEditRow;